/**
 * components/brief-builder/BriefBuilderView.tsx
 *
 * Root component for the Brief Builder module.
 * Orchestrates the multi-step wizard based on session.stage.
 */
import { useEffect, useRef, useState } from 'react'
import type { BriefSession } from '@/types/brief-builder'
import { useBriefBuilder } from '@/hooks/useBriefBuilder'

import StepIndicator        from './StepIndicator'
import BriefIntakeStep      from './BriefIntakeStep'
import BriefInterpretStep   from './BriefInterpretStep'
import BriefRecommendStep   from './BriefRecommendStep'
import BriefConfigStep      from './BriefConfigStep'
import BriefExportStep      from './BriefExportStep'

// ── Pipeline progress definitions ─────────────────────────────────────────────

type PipelineStep = { label: string; detail: string; durationMs: number }

const INTERPRET_STEPS: PipelineStep[] = [
  { label: 'Parsing brief',           detail: 'Extracting tenant name, industry and markets…',      durationMs: 3_500 },
  { label: 'Identifying stakeholders',detail: 'Mapping roles and departments from your description…',durationMs: 4_500 },
  { label: 'Extracting goals',        detail: 'Pulling business goals and use-cases…',               durationMs: 4_000 },
  { label: 'Generating questions',    detail: 'Preparing clarification questions for missing info…', durationMs: 5_000 },
  { label: 'Finalising',              detail: 'Almost there…',                                       durationMs: 99_000 },
]

const RECOMMEND_STEPS: PipelineStep[] = [
  { label: 'Scoring dashboard families', detail: 'Matching stakeholder roles to widget categories…', durationMs: 2_500 },
  { label: 'Selecting widgets',          detail: 'Building 2×3 grid for each dashboard…',            durationMs: 4_000 },
  { label: 'Enriching rationale',        detail: 'Writing rationale for each recommendation…',       durationMs: 5_000 },
  { label: 'Finalising',                 detail: 'Almost there…',                                    durationMs: 99_000 },
]

// ── Animated pipeline progress overlay ────────────────────────────────────────

function PipelineProgress({ steps }: { steps: PipelineStep[] }) {
  const [activeIdx, setActiveIdx] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setActiveIdx(0)
  }, [steps])

  useEffect(() => {
    if (activeIdx >= steps.length - 1) return
    timerRef.current = setTimeout(() => {
      setActiveIdx(i => Math.min(i + 1, steps.length - 1))
    }, steps[activeIdx].durationMs)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [activeIdx, steps])

  const current = steps[activeIdx]

  return (
    <div className="flex flex-col items-center justify-center py-24 px-6 gap-8 max-w-md mx-auto">
      {/* Spinner */}
      <div className="relative w-14 h-14">
        <div className="absolute inset-0 rounded-full border-4 border-indigo-100" />
        <div className="absolute inset-0 rounded-full border-4 border-t-indigo-600
                        border-r-indigo-300 border-b-transparent border-l-transparent animate-spin" />
      </div>

      {/* Current step label */}
      <div className="text-center space-y-1">
        <p className="text-base font-semibold text-slate-800">{current.label}</p>
        <p className="text-sm text-slate-400">{current.detail}</p>
      </div>

      {/* Step dots */}
      <div className="flex flex-col gap-2 w-full">
        {steps.map((step, idx) => {
          const done    = idx < activeIdx
          const active  = idx === activeIdx
          const pending = idx > activeIdx
          return (
            <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all
              ${ active  ? 'bg-indigo-50 border border-indigo-200' :
                 done    ? 'opacity-60' : 'opacity-30' }`}
            >
              {/* Icon */}
              <div className={`flex-none w-5 h-5 rounded-full flex items-center justify-center text-[10px]
                ${ done   ? 'bg-emerald-500 text-white' :
                   active ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400' }`}
              >
                {done ? '✓' : idx + 1}
              </div>
              {/* Label */}
              <span className={`text-xs font-medium
                ${ done   ? 'text-emerald-700 line-through decoration-emerald-300' :
                   active ? 'text-indigo-700' : 'text-slate-400' }`}
              >
                {step.label}
              </span>
              {/* Animated dot for active */}
              {active && (
                <span className="ml-auto flex gap-0.5">
                  {[0,1,2].map(i => (
                    <span key={i} className="w-1 h-1 rounded-full bg-indigo-400 animate-bounce"
                          style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Simple fallback spinner (for short operations) ────────────────────────────

function BusyOverlay({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <div className="w-10 h-10 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  )
}

// ── Session list ──────────────────────────────────────────────────────────────

function SessionList({
  sessions,
  onOpen,
  onNew,
  onDelete,
}: {
  sessions: ReturnType<typeof useBriefBuilder>['sessions']
  onOpen: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const stageColor: Record<string, string> = {
    complete:   'text-emerald-600 bg-emerald-50 border-emerald-200',
    error:      'text-red-600 bg-red-50 border-red-200',
    reviewing:  'text-indigo-600 bg-indigo-50 border-indigo-200',
    validated:  'text-violet-600 bg-violet-50 border-violet-200',
    configured: 'text-violet-600 bg-violet-50 border-violet-200',
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Brief Sessions</h2>
        <button
          onClick={onNew}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                     hover:bg-indigo-700 transition-colors"
        >
          + New Brief
        </button>
      </div>
      {sessions.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          No sessions yet — create a new brief to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div
              key={s.id}
              className="w-full flex items-center gap-2 px-4 py-3
                         rounded-xl border border-slate-200 bg-white group
                         hover:border-indigo-200 transition-colors"
            >
              {/* Clickable row content */}
              <button
                onClick={() => onOpen(s.id)}
                className="flex-1 text-left flex items-center justify-between min-w-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 group-hover:text-indigo-700 truncate">
                    {s.label}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {new Date(s.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`ml-3 shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border
                  ${stageColor[s.stage] ?? 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                  {s.stage}
                </span>
              </button>

              {/* Delete control */}
              {confirmId === s.id ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] text-slate-500">Delete?</span>
                  <button
                    onClick={() => { onDelete(s.id); setConfirmId(null) }}
                    className="text-[11px] font-semibold text-red-600 hover:text-red-700 px-2 py-1
                               rounded bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1
                               rounded bg-slate-100 hover:bg-slate-200 transition-colors"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  onClick={e => { e.stopPropagation(); setConfirmId(s.id) }}
                  title="Delete session"
                  className="shrink-0 opacity-0 group-hover:opacity-100 p-1.5 rounded-lg
                             text-slate-300 hover:text-red-400 hover:bg-red-50 transition-all"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 3.5h10M5.5 3.5V2.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1M3.5 3.5l.5 8h6l.5-8"
                          stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main view ──────────────────────────────────────────────────────────────────

export default function BriefBuilderView() {
  const {
    briefView,
    sessions,
    widgets,
    error,
    lastExport,
    loadWidgets,
    loadSessions,
    createSession,
    openSession,
    submitClarifications,
    saveStructure,
    validateSession,
    submitModuleConfig,
    exportSession,
    deleteSession,
    goToNew,
    goToList,
  } = useBriefBuilder()

  // Load widget library once on mount
  useEffect(() => { loadWidgets() }, [loadWidgets])

  // ── Error banner ───────────────────────────────────────────────────────────
  const ErrorBanner = error ? (
    <div className="bg-red-50 border-b border-red-200 px-5 py-2 text-xs text-red-700">
      {error}
    </div>
  ) : null

  // ── Top tab bar (New | History) ────────────────────────────────────────────
  const tabBar = (
    <div className="border-b border-slate-100 bg-white">
      <div className="max-w-6xl mx-auto px-5 flex gap-0.5 pt-3">
        {(['new', 'history'] as const).map(tab => {
          const active = tab === 'new'
            ? briefView.view !== 'list'
            : briefView.view === 'list'
          return (
            <button
              key={tab}
              onClick={tab === 'new' ? goToNew : goToList}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors capitalize
                ${active
                  ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
            >
              {tab === 'new' ? 'New Brief' : 'History'}
            </button>
          )
        })}
      </div>
    </div>
  )

  // ── Render based on view state ─────────────────────────────────────────────

  if (briefView.view === 'list') {
    return (
      <>
        {tabBar}
        {ErrorBanner}
        <SessionList sessions={sessions} onOpen={openSession} onNew={goToNew} onDelete={deleteSession} />
      </>
    )
  }

  if (briefView.view === 'busy') {
    const msg = briefView.message.toLowerCase()
    const isLongOp = msg.includes('recommend') || msg.includes('generat') || msg.includes('interpret')
    const steps = msg.includes('recommend') ? RECOMMEND_STEPS : INTERPRET_STEPS
    return (
      <>
        {briefView.session && <StepIndicator stage={briefView.session.stage} />}
        {isLongOp
          ? <PipelineProgress steps={steps} />
          : <BusyOverlay message={briefView.message} />}
      </>
    )
  }

  if (briefView.view === 'new') {
    return (
      <>
        {tabBar}
        {ErrorBanner}
        <BriefIntakeStep onSubmit={createSession} />
      </>
    )
  }

  // ── Session view — route to the right step component ──────────────────────
  if (briefView.view === 'session') {
    const session: BriefSession = briefView.session
    const stage = session.stage

    return (
      <>
        {tabBar}
        {ErrorBanner}
        <StepIndicator stage={stage} />

        {/* Intake — should not normally be shown once session exists */}
        {stage === 'intake' && (
          <BriefIntakeStep onSubmit={createSession} />
        )}

        {/* Interpreting: animated pipeline progress */}
        {stage === 'interpreting' && (
          <PipelineProgress steps={INTERPRET_STEPS} />
        )}

        {/* Interpreted: show requirements + clarification questions */}
        {stage === 'interpreted' && (
          <BriefInterpretStep
            session={session}
            onSubmitClarifications={(answers) =>
              submitClarifications(session.id, answers)
            }
          />
        )}

        {/* Recommending: animated pipeline progress */}
        {stage === 'recommending' && (
          <PipelineProgress steps={RECOMMEND_STEPS} />
        )}

        {/* Reviewing: show dashboard editor */}
        {stage === 'reviewing' && (
          <BriefRecommendStep
            session={session}
            widgets={widgets}
            onSave={(payload) => saveStructure(session.id, payload)}
            onValidate={() => validateSession(session.id)}
          />
        )}

        {/* Validating: busy */}
        {stage === 'validating' && (
          <BusyOverlay message="Validating business rules…" />
        )}

        {/* Validated → Configuration step (optional AI module setup) */}
        {stage === 'validated' && (
          <BriefConfigStep
            session={session}
            onSave={(payload) => submitModuleConfig(session.id, payload)}
          />
        )}

        {/* Configured / Exporting / Complete → Export step */}
        {(stage === 'configured' || stage === 'exporting' || stage === 'complete') && (
          <BriefExportStep
            session={session}
            lastExport={lastExport}
            onExport={() => exportSession(session.id)}
            onBackToEdit={() => saveStructure(session.id, {
              modules: session.recommendation?.modules ?? ['dashboards'],
              dashboards: session.approved_structure?.dashboards ?? session.recommendation?.dashboards ?? [],
              clarification_answers: session.clarification_answers,
            })}
          />
        )}

        {/* Error */}
        {stage === 'error' && (
          <div className="max-w-2xl mx-auto px-5 py-16 text-center space-y-4">
            <div className="text-4xl">⚠️</div>
            <p className="text-base font-semibold text-slate-800">Something went wrong</p>
            <p className="text-sm text-slate-500">{session.error}</p>
            <button
              onClick={goToNew}
              className="px-5 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium
                         hover:bg-slate-200 transition-colors"
            >
              Start a new brief
            </button>
          </div>
        )}
      </>
    )
  }

  return null
}
